/**
 * Batch Transfer Component
 * Send funds to multiple recipients in one transaction
 * Supports scheduling for future execution
 */
import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { previewBatchTransfer, executeBatchTransfer, parseBatchCSV } from '../lib/api';
import { toast } from 'sonner';
import { 
  Users, 
  Plus, 
  Trash2, 
  Upload, 
  Send, 
  AlertTriangle,
  CheckCircle,
  XCircle,
  Loader2,
  FileSpreadsheet,
  Wallet,
  Calendar,
  Clock
} from 'lucide-react';

const CURRENCIES = ['USD', 'EUR', 'GBP', 'NGN', 'GHS', 'KES', 'ZAR'];
const MAX_RECIPIENTS = 100;

const BatchTransfer = ({ wallets = {} }) => {
  const [recipients, setRecipients] = useState([{ email: '', tag: '', amount: '', note: '' }]);
  const [currency, setCurrency] = useState('USD');
  const [globalNote, setGlobalNote] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');

  const addRecipient = () => {
    if (recipients.length >= MAX_RECIPIENTS) {
      toast.error(`Maximum ${MAX_RECIPIENTS} recipients allowed`);
      return;
    }
    setRecipients([...recipients, { email: '', tag: '', amount: '', note: '' }]);
  };

  const removeRecipient = (index) => {
    if (recipients.length === 1) return;
    setRecipients(recipients.filter((_, i) => i !== index));
  };

  const updateRecipient = (index, field, value) => {
    const updated = [...recipients];
    updated[index][field] = value;
    setRecipients(updated);
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await parseBatchCSV(formData);
      const data = response.data;
      
      if (data.recipients && data.recipients.length > 0) {
        setRecipients(data.recipients.map(r => ({
          email: r.email || '',
          tag: r.tag || '',
          amount: r.amount?.toString() || '',
          note: r.note || ''
        })));
        toast.success(`Loaded ${data.recipients.length} recipients from CSV`);
        
        if (data.errors && data.errors.length > 0) {
          toast.warning(`${data.errors.length} rows had errors and were skipped`);
        }
      } else {
        toast.error('No valid recipients found in CSV');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to parse CSV');
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  };

  const handlePreview = async () => {
    // Filter out empty rows
    const validRecipients = recipients.filter(r => 
      (r.email?.trim() || r.tag?.trim()) && r.amount
    );
    
    if (validRecipients.length === 0) {
      toast.error('Add at least one recipient with email/tag and amount');
      return;
    }

    setLoading(true);
    try {
      const response = await previewBatchTransfer({
        recipients: validRecipients.map(r => ({
          email: r.email?.trim() || null,
          tag: r.tag?.trim() || null,
          amount: parseFloat(r.amount),
          note: r.note?.trim() || null
        })),
        currency
      });
      
      setPreview(response.data);
      
      if (!response.data.can_proceed) {
        if (!response.data.summary.has_sufficient_funds) {
          toast.error('Insufficient funds for this batch transfer');
        } else {
          toast.warning(`${response.data.errors.length} recipient(s) have validation errors`);
        }
      } else {
        setShowConfirmDialog(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to preview batch transfer');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!preview?.can_proceed) return;

    setSending(true);
    try {
      const validRecipients = recipients.filter(r => 
        (r.email?.trim() || r.tag?.trim()) && r.amount
      );
      
      // Build request payload
      const payload = {
        recipients: validRecipients.map(r => ({
          email: r.email?.trim() || null,
          tag: r.tag?.trim() || null,
          amount: parseFloat(r.amount),
          note: r.note?.trim() || null
        })),
        currency,
        note: globalNote || null
      };
      
      // Add scheduled_at if scheduling is enabled
      if (isScheduled && scheduledDate && scheduledTime) {
        const scheduledAt = new Date(`${scheduledDate}T${scheduledTime}`);
        payload.scheduled_at = scheduledAt.toISOString();
      }
      
      const response = await executeBatchTransfer(payload);
      
      if (isScheduled) {
        toast.success(`Batch transfer scheduled for ${scheduledDate} ${scheduledTime}!`);
      } else {
        toast.success(`Successfully sent to ${response.data.summary.successful} recipients!`);
      }
      
      setShowConfirmDialog(false);
      setRecipients([{ email: '', tag: '', amount: '', note: '' }]);
      setPreview(null);
      setGlobalNote('');
      setIsScheduled(false);
      setScheduledDate('');
      setScheduledTime('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Batch transfer failed');
    } finally {
      setSending(false);
    }
  };

  const walletBalance = wallets[currency] || 0;
  const totalAmount = recipients.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);

  return (
    <>
      <Card data-testid="batch-transfer-card">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Users className="w-5 h-5 text-emerald-500" />
            Batch Transfer
          </CardTitle>
          <CardDescription>
            Send funds to multiple recipients in one transaction (max {MAX_RECIPIENTS})
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Currency and CSV Upload */}
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[150px]">
              <Label>Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger data-testid="batch-currency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((curr) => (
                    <SelectItem key={curr} value={curr}>{curr}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Wallet className="w-4 h-4" />
              Balance: <span className="font-medium">${walletBalance.toLocaleString()}</span>
            </div>

            <div>
              <input
                type="file"
                accept=".csv"
                onChange={handleCSVUpload}
                className="hidden"
                id="csv-upload"
                disabled={loading}
              />
              <Button
                variant="outline"
                onClick={() => document.getElementById('csv-upload').click()}
                disabled={loading}
                data-testid="csv-upload-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Import CSV
              </Button>
            </div>
          </div>

          {/* CSV Format Hint */}
          <div className="text-xs text-muted-foreground flex items-center gap-2 p-2 rounded-lg bg-muted">
            <FileSpreadsheet className="w-4 h-4" />
            CSV format: email, amount, note (optional) — or — tag, amount, note
          </div>

          {/* Recipients List */}
          <div className="space-y-3">
            <Label>Recipients ({recipients.length}/{MAX_RECIPIENTS})</Label>
            
            <div className="max-h-[400px] overflow-y-auto space-y-2 pr-2">
              {recipients.map((recipient, index) => (
                <div key={index} className="flex gap-2 items-start p-3 rounded-xl bg-muted/50">
                  <div className="flex-1 grid grid-cols-2 gap-2">
                    <Input
                      placeholder="Email"
                      value={recipient.email}
                      onChange={(e) => updateRecipient(index, 'email', e.target.value)}
                      className="h-9"
                      data-testid={`recipient-email-${index}`}
                    />
                    <Input
                      placeholder="@tag"
                      value={recipient.tag}
                      onChange={(e) => updateRecipient(index, 'tag', e.target.value)}
                      className="h-9"
                      data-testid={`recipient-tag-${index}`}
                    />
                    <Input
                      type="number"
                      placeholder="Amount"
                      value={recipient.amount}
                      onChange={(e) => updateRecipient(index, 'amount', e.target.value)}
                      className="h-9"
                      min="0"
                      step="0.01"
                      data-testid={`recipient-amount-${index}`}
                    />
                    <Input
                      placeholder="Note (optional)"
                      value={recipient.note}
                      onChange={(e) => updateRecipient(index, 'note', e.target.value)}
                      className="h-9"
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeRecipient(index)}
                    disabled={recipients.length === 1}
                    className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                    data-testid={`remove-recipient-${index}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>

            <Button
              variant="outline"
              onClick={addRecipient}
              disabled={recipients.length >= MAX_RECIPIENTS}
              className="w-full"
              data-testid="add-recipient-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Recipient
            </Button>
          </div>

          {/* Global Note */}
          <div>
            <Label>Note for all transfers (optional)</Label>
            <Textarea
              placeholder="This note will apply to all transfers in this batch"
              value={globalNote}
              onChange={(e) => setGlobalNote(e.target.value)}
              className="mt-2"
            />
          </div>

          {/* Schedule Option */}
          <div className="p-4 rounded-xl bg-muted/50 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-emerald-500" />
                <div>
                  <Label className="text-base">Schedule for later</Label>
                  <p className="text-xs text-muted-foreground">Set a future date/time for execution</p>
                </div>
              </div>
              <Switch
                checked={isScheduled}
                onCheckedChange={setIsScheduled}
                data-testid="schedule-toggle"
              />
            </div>
            
            {isScheduled && (
              <div className="grid grid-cols-2 gap-3 pt-2">
                <div>
                  <Label className="text-xs text-muted-foreground">Date</Label>
                  <Input
                    type="date"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="mt-1"
                    data-testid="schedule-date"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Time</Label>
                  <Input
                    type="time"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                    className="mt-1"
                    data-testid="schedule-time"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Summary */}
          <div className="p-4 rounded-xl bg-muted space-y-2">
            <div className="flex justify-between text-sm">
              <span>Total Recipients</span>
              <span className="font-medium">{recipients.filter(r => r.email || r.tag).length}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Total Amount</span>
              <span className="font-medium">${totalAmount.toFixed(2)} {currency}</span>
            </div>
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Estimated Fees (0.5%)</span>
              <span>${(totalAmount * 0.005).toFixed(2)} {currency}</span>
            </div>
            <div className="flex justify-between font-medium pt-2 border-t border-border">
              <span>Estimated Total</span>
              <span>${(totalAmount * 1.005).toFixed(2)} {currency}</span>
            </div>
          </div>

          {/* Preview Errors */}
          {preview?.errors?.length > 0 && (
            <div className="p-3 rounded-xl bg-red-500/10 text-red-600 text-sm">
              <div className="flex items-center gap-2 font-medium mb-2">
                <AlertTriangle className="w-4 h-4" />
                Validation Errors
              </div>
              <ul className="list-disc list-inside space-y-1">
                {preview.errors.map((err, i) => (
                  <li key={i}>Row {err.index + 1}: {err.error}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Send Button */}
          <Button
            onClick={handlePreview}
            disabled={loading || recipients.filter(r => r.email || r.tag).length === 0}
            className="w-full h-12 rounded-xl"
            data-testid="batch-preview-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Validating...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Preview & Send
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Confirm Batch Transfer</DialogTitle>
            <DialogDescription>
              Review the transfer details before sending
            </DialogDescription>
          </DialogHeader>

          {preview && (
            <div className="space-y-4 py-4">
              {/* Summary */}
              <div className="p-4 rounded-xl bg-muted space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Recipients</span>
                  <Badge variant="secondary">{preview.summary.valid_count} valid</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Amount</span>
                  <span className="font-medium">${preview.summary.total_amount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Fees</span>
                  <span>${preview.summary.total_fees.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-medium text-lg pt-2 border-t border-border">
                  <span>Grand Total</span>
                  <span className="text-emerald-600">${preview.summary.grand_total.toFixed(2)} {preview.summary.currency}</span>
                </div>
              </div>

              {/* Recipients Preview */}
              <div className="max-h-[200px] overflow-y-auto space-y-2">
                {preview.recipients.filter(r => r.valid).map((r, i) => (
                  <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-muted/50 text-sm">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500" />
                      <span className="truncate max-w-[150px]">{r.recipient_name || r.email || `@${r.tag}`}</span>
                    </div>
                    <span className="font-medium">${r.amount.toFixed(2)}</span>
                  </div>
                ))}
              </div>

              {/* Balance Check */}
              <div className={`flex items-center gap-2 p-3 rounded-xl ${
                preview.summary.has_sufficient_funds 
                  ? 'bg-emerald-500/10 text-emerald-700' 
                  : 'bg-red-500/10 text-red-600'
              }`}>
                {preview.summary.has_sufficient_funds ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm">Sufficient balance (${preview.summary.wallet_balance.toFixed(2)} available)</span>
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4" />
                    <span className="text-sm">Insufficient balance (${preview.summary.wallet_balance.toFixed(2)} available)</span>
                  </>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSend}
              disabled={sending || !preview?.can_proceed}
              data-testid="batch-confirm-btn"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send ${preview?.summary.grand_total.toFixed(2)}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BatchTransfer;
